<template>
  <div :class="$setClasses">
    <div class="u-display-flex u-ai-center">
      <h2 v-if="field.title">{{ $te(field.title) ? $t(field.title) : field.title }}</h2>
      <nxt-core-tooltip
        v-if="field && field.button_refresh"
        :content="$t('base.tooltip.refreshCalcEstimate')"
        not-disabled-tooltip
        placement="top-start"
      >
        <nxt-tasks-btn
          :disabled="estimateLoading"
          circle
          class="el-button--default nxt-button__ghost button"
          @click="calc_estimation"
        >
          <font-awesome-icon :icon="['fas', 'redo']" class="fa-fw" />
        </nxt-tasks-btn>
      </nxt-core-tooltip>
    </div>
    <nxt-core-html-content v-if="!estimateLoading">
      <template v-if="field.before_content" v-slot:beforeContent>
        <div v-html="field.before_content"></div>
      </template>
      <template v-if="estimation && estimation.volume && estimation.distance">
        {{
          $t("estimate.message", {
            volume: $filters.format.convertToInteger(estimation.volume, "m³"),
            distance: estimation.distance,
          })
        }}
      </template>
      <div :class="$vClass('body')">
        <template
          v-if="formattedEstimate && formattedEstimate.length"
          v-for="(item, index) in formattedEstimate"
        >
          <nxt-core-card
            v-if="item && item.title"
            :class="[$vClass('body__estim'), '--padding-none --margin-top']"
          >
            <div :class="[$vClass('body__estim__content'), `--${item.bg_color}`]">
              <h2 :class="$vClass('body__estim__content__title')">{{ item.title }}</h2>
              <p :class="$vClass('body__estim__content__price')">
                {{
                  $t("estimate.price", {
                    low: item.low,
                    high: item.high,
                  })
                }}
              </p>
            </div>
            <nxt-core-toggle :initToggle="item.initInfo">
              <ul :class="$vClass('body__estim__list')">
                <template v-for="(text, i) in item.description" :key="`icon-${i}`">
                  <li v-if="i > 0" :class="$vClass('body__estim__list__icon')">
                    <font-awesome-icon :icon="['fal', 'circle-plus']" />
                  </li>
                  <li
                    :class="
                      index !== 0 &&
                      i === 0 &&
                      $vClass(`body__estim__list__item-important --${item.bg_color}`)
                    "
                  >
                    {{ text }}
                  </li>
                </template>
              </ul>
            </nxt-core-toggle>
          </nxt-core-card>
        </template>
      </div>
      <template v-if="field.after_content" v-slot:afterContent>
        <div v-html="field.after_content" />
      </template>
    </nxt-core-html-content>
    <div v-else>
      <nxt-core-loader :text="$t('estimate.loading')" size-loader="--size-small" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useProjectsStore } from "@nextories/vue-tasks/src/store/projectsStore.ts";
import { useEstimateStore } from "@nextories/vue-core-components/src/store/estimateStore.ts";

export default {
  name: "Estimate",
  nameClassStyle: "price-card",
  props: {
    field: Object,
    prefix: String,
  },
  computed: {
    ...mapState(useProjectsStore, ["currentStep", "steps", "project"]),
    ...mapState(useEstimateStore, ["estimateLoading", "estimation"]),
    formattedEstimate() {
      this.field.value = this.estimation;
      return (
        this.estimation &&
        this.estimation.price &&
        Object.entries(this.estimation.price).map((key) => ({
          ...key[1],
          ...this.$tm("estimate.formula")[key[0]],
          ...this.field.estimations[key[0]],
        }))
      );
    },
  },
  methods: {
    ...mapActions(useEstimateStore, ["calcEstimation"]),
    async calc_estimation() {
      try {
        const data = this.$helpers.fieldUtils.cleanData([this.currentStep]);
        data.formData.token = this.project.token;
        await this.calcEstimation(data, false);
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$colors: (
  "grey-blue": var(--nxt-color-grey-blue),
  "lynch": var(--nxt-color-lynch),
  "santas-gray": var(--nxt-color-santas-gray),
  "ghost": var(--nxt-color-ghost),
);
.#{$prefixName}price-card {
  h2 {
    margin: 0;
  }

  &__body {
    display: flex;
    flex-flow: wrap;
    margin: auto;
    max-width: 900px;
    justify-content: space-around;

    @include breakpoint(tablet) {
      &__estim {
        width: 250px;
        height: 100%;
        margin-bottom: var(--nxt-space-medium);
      }
    }

    &__estim {
      margin: auto;
      border-radius: var(--nxt-border-radius-base);
      max-width: 155px;
      width: 100%;

      &.--margin-top {
        margin-top: var(--nxt-space-default);
      }

      @include breakpoint(tablet) {
        width: 25%;
        margin: 0;
        &.--margin-top {
          margin-top: 0;
        }
      }
      @each $name, $color in $colors {
        &__content.--color-#{$name} {
          background-color: $color;
        }
        &__list__item-important.--color-#{$name} {
          color: $color;
        }
      }

      &__list__item-important {
        font-weight: var(--nxt-font-weight-bold);
      }

      &__content {
        font-weight: var(--nxt-font-weight-bold);
        background-color: var(--nxt-color-background-sub-menu-light);
        padding: var(--nxt-space-small) var(--nxt-space-xsmall);
        text-align: center;

        &__title {
          text-align: center;
          text-transform: uppercase;
          color: var(--nxt-color-white);
          font-size: var(--nxt-font-size-medium);
          margin: var(--nxt-space-small) 0;
        }

        &__price {
          text-align: center;
          color: var(--nxt-color-secondary);
          white-space: normal;
          margin: var(--nxt-space-small) 0;
          font-size: var(--nxt-font-size-default);
        }
      }

      &__list {
        padding: 0 var(--nxt-space-small);
        font-size: var(--nxt-font-size-default);
        text-align: center;
        list-style-type: none;

        &__icon {
          color: var(--nxt-color-primary);
          font-size: var(--nxt-font-size-medium);
          padding: var(--nxt-space-small) 0;
        }
      }
    }
  }

  :deep(.#{$prefixName}price-card__body__notice) {
    padding-top: 20px;
    font-size: var(--nxt-font-size-medium);
  }

  :deep(.#{$prefixName}toggle) {
    padding: 0;
  }
}
</style>
